import React, { useState } from 'react';
import { User, Mail, Building, Edit3, Save, X, Loader2, CheckCircle, AlertCircle, Settings } from 'lucide-react';
import { useUIState } from '@/core/UIStateProvider';
import { useUpdateUser, useUserProfile } from '@/core/api/queries';

interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  shipmentUpdates: boolean;
}

interface Profile {
  id: string;
  name: string;
  email: string;
  role: 'USER' | 'ADMIN' | 'SUPER_ADMIN';
  company?: {
    name: string;
  };
  notificationPreferences?: NotificationPreferences;
  createdAt: string;
  updatedAt: string;
}

interface ProfileFormData {
  companyId?: string;
  email?: string;
  name?: string;
  role?: string;
  notificationPreferences?: object;
  walletAddress?: string;
}

interface ProfileDisplayProps {
  profile: Profile | undefined;
  onEdit: () => void;
}

interface ProfileEditFormProps {
  profile: Profile | undefined;
  onSave: (data: ProfileFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

// Profile Display Component
const ProfileDisplay: React.FC<ProfileDisplayProps> = ({ profile, onEdit }) => (
  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
    {/* Header Section */}
    <div className="bg-gradient-to-br from-blue-500 to-purple-600 px-8 py-10 text-white relative">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-6">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
            <User size={36} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold mb-2">{profile?.name || 'User Name'}</h1>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${profile?.role === 'SUPER_ADMIN' ? 'bg-purple-200 text-purple-900' :
                profile?.role === 'ADMIN' ? 'bg-blue-200 text-blue-900' :
                  'bg-green-200 text-green-900'
                }`}>
                {profile?.role || 'USER'}
              </span>
              {profile?.company?.name && (
                <span className="text-white/80 text-sm">{profile.company.name}</span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 border border-white/30 transition-all"
        >
          <Edit3 size={18} />
          <span>Edit Profile</span>
        </button>
      </div>
    </div>

    {/* Details Section */}
    <div className="p-8 space-y-8">
      {/* Contact Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Mail className="mr-2 text-blue-500" size={20} />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Email Address</label>
            <p className="mt-1 text-gray-900 font-medium">{profile?.email || 'Not set'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Company</label>
            <p className="mt-1 text-gray-900 font-medium">{profile?.company?.name || 'Not assigned'}</p>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Settings className="mr-2 text-blue-500" size={20} />
          Notification Preferences
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'email', label: 'Email Notifications' },
            { key: 'sms', label: 'SMS Notifications' },
            { key: 'shipmentUpdates', label: 'Shipment Updates' }
          ].map(({ key, label }) => (
            <div key={key} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${profile?.notificationPreferences?.[key as keyof NotificationPreferences]
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-200 text-gray-600'
                }`}>
                {profile?.notificationPreferences?.[key as keyof NotificationPreferences] ? 'On' : 'Off'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Account Details */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Account Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Created:</span>
            <span className="ml-2 text-gray-900">{profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Unknown'}</span>
          </div>
          <div>
            <span className="text-gray-500">Last Updated:</span>
            <span className="ml-2 text-gray-900">{profile?.updatedAt ? new Date(profile.updatedAt).toLocaleDateString() : 'Unknown'}</span>
          </div>
          <div>
            <span className="text-gray-500">User ID:</span>
            <span className="ml-2 font-mono text-gray-900">{profile?.id || 'Unknown'}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Profile Edit Form Component
const ProfileEditForm: React.FC<ProfileEditFormProps> = ({ profile, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<ProfileFormData>({
    name: profile?.name || '',
    email: profile?.email || '',
    notificationPreferences: {
      email: profile?.notificationPreferences?.email || false,
      sms: profile?.notificationPreferences?.sms || false,
      shipmentUpdates: profile?.notificationPreferences?.shipmentUpdates || false
    }
  });

  const handleInputChange = (field: keyof Pick<ProfileFormData, 'name' | 'email'>, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationChange = (key: keyof NotificationPreferences, value: boolean): void => {
    setFormData(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        [key]: value
      }
    }));
  };

  const handleSave = (): void => {
    onSave(formData);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Form Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-8 py-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Edit3 size={24} />
            <h2 className="text-xl font-semibold">Edit Profile</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-300 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-8 space-y-8">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <User className="mr-2 text-blue-500" size={20} />
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your email address"
              />
            </div>
          </div>
        </div>

        {/* Read-only Company Info */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building className="mr-2 text-blue-500" size={20} />
            Company Information
          </h3>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-500">Company</span>
                <p className="text-gray-900 font-medium">{profile?.company?.name || 'Not assigned'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Role</span>
                <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${profile?.role === 'SUPER_ADMIN' ? 'bg-purple-100 text-purple-800' :
                  profile?.role === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                  {profile?.role || 'USER'}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Company and role information is managed by your administrator</p>
          </div>
        </div>


        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium flex items-center space-x-2"
          >
            {isLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const UserProfilePage: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const { addNotification, notifications } = useUIState();
  const userProfile = useUserProfile()
  const updateUserProfile = useUpdateUser()


  const handleSave = async (formData: ProfileFormData): Promise<void> => {
    try {
      await updateUserProfile.mutateAsync({ userId: userProfile.data?.id as string, data: formData });
      addNotification('success', 'Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      addNotification('error', errorMessage);
    }
  };

  const handleCancel = (): void => {
    setIsEditing(false);
  };

  const currentNotification = notifications[notifications.length - 1];

  if (userProfile.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 flex items-center space-x-3">
          <Loader2 size={24} className="animate-spin text-blue-500" />
          <span className="text-gray-700 font-medium">Loading your profile...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Status Notification */}
      {currentNotification && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg border ${currentNotification.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
            }`}>
            {currentNotification.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span className="font-medium">{currentNotification.message}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {isEditing ? (
            <ProfileEditForm
              profile={userProfile.data as Profile}
              onSave={handleSave}
              onCancel={handleCancel}
              isLoading={updateUserProfile?.isPending}
            />
          ) : (
            <ProfileDisplay
              profile={userProfile.data as Profile}
              onEdit={() => setIsEditing(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;